import { createStore } from 'vuex';
import qs from 'qs';

import formData from '@component_path/slide_form_common_module/store/formData';
import requestParam from '@component_path/slide_form_common_module/store/requestParam';
import masterData from '@component_path/slide_form_common_module/store/masterData';
import eopContents from '@component_path/slide_form_common_module/store/eopContents';
import { handlePostInquiryMail, redirectToThanksPageUrlWithParams } from '@component_path/lib/call_api/inquiry_mail/handlePostInquiryMail';
import positionInformation from '@component_path/slide_form_common_module/store/positionInformation';
import recaptchaSetting from '@component_path/slide_form_common_module/store/recaptchaSetting';

const rootGetters = {
  cityText(state) {
    const city = state.masterData.cities.find(city => city.value === state.formData.city_id);
    return city ? city.label : "";
  },
  employmentTypeText(state) {
    const employmentType = state.masterData.employment_types.find(employment_type => employment_type.value === state.formData.employment_type);
    return employmentType ? employmentType.label : "";
  },
  prefectureText(state) {
    const prefecture = state.masterData.prefectures.find(prefecture => prefecture.value === state.formData.prefecture_id);
    return prefecture ? prefecture.label : "";
  },
  positionTypeText(state) {
    const prefecture = state.masterData.prefectures.find(prefecture => prefecture.value === state.formData.prefecture_id);
    return prefecture ? prefecture.label : "";
  },
  wishfulTimeText(state) {
    const wishfulTime = state.masterData.wishful_times.find(wishful_time => wishful_time.value === state.formData.wishful_time);
    return wishfulTime ? wishfulTime.label : "";
  },
  ageText(state) {
    const age = state.masterData.birth_years.find(birth_year => birth_year.value === state.formData.age);
    return age ? age.label : "";
  },
  workStatusText(state) {
    const workStatus = state.masterData.work_statuses.find(work_status => work_status.value === state.formData.work_status);
    return workStatus ? workStatus.label : "";
  },
  sexText(state) {
    const sex = state.masterData.sexes.find(sex => sex.value === state.formData.sex);
    return sex ? sex.label : "";
  },
  experiencesAttributesText(state) {
    const prefecture = state.masterData.prefectures.find(prefecture => prefecture.value === state.formData.prefecture_id);
    return prefecture ? prefecture.label : "";
  },
  // agent-selection の提携会社一覧を取得するためのリクエストパラメータを返す
  auctionQueryParams: (state, getters) => (formParamKeys) => {
    return createRequestParams(state, getters, formParamKeys);
  }
};

function submitParams(state, getters) {
  const postFormData = {};
  state.masterData.post_item_keys.map(itemKey => {
    if (typeof itemKey === 'object') {
      Object.keys(itemKey).map(subItemKey => {
        postFormData[subItemKey] = state.formData[subItemKey];
      });
    } else {
      postFormData[itemKey] = state.formData[itemKey];
    }
  });

  // nameとname_kanaはvuexにstoreを用意していないので独自にパラメータに入れる必要がある
  postFormData['name'] = getters['formData/name'];
  postFormData['name_kana'] = getters['formData/name_kana'];
  if (state.masterData.post_item_keys.includes('address')) {
    postFormData['address'] = getters['cityText'];
  }

  const requestBody = {
    [state.masterData.form_param_key]: postFormData,
    authenticity_token: state.requestParam.authenticity_token
  };

  try {
    if (typeof (grecaptcha) === 'object') {
      requestBody['g-recaptcha-response'] = grecaptcha.getResponse();
    }
  }
  catch (e) {
    // recaptchaを読み込んでないページでgrecaptcha.getResponse()がエラーを吐いて問い合わせ処理が
    // 続行できなくなるため例外処理を書いて問い合わせを続行させる
    // TODO: No reCAPTCHA client existsというエラーを解消する
  }

  return qs.stringify(
    requestBody,
    { arrayFormat: 'brackets' }
  );
}

// サーバ側にリクエストするために必要な項目をオブジェクトにまとめる。
function createRequestParams(state, getters, formParamKeys) {
  const formParams = Object.assign({}, state.formData);
  delete formParams.authenticity_token;

  const cityId = state.city_id;
  if (cityId) {
    // フォーム内では一貫してcity_idで処理を行う。
    // しかし、サーバ側が要求するのは市区町村名なので、ここでテキスト変換、項目への設定を行う。
    formParams.address = getters.getCityName(cityId);
  }

  // フォーム(controller)ごとに必要な項目は異なる。
  // 不要な項目をポストしてしまわないように、ここで項目をチェックする
  for (const key of Object.getOwnPropertyNames(formParams)) {
    // console.log(`key: ${key}, value: ${formParams[key]}`)

    if (!formParamKeys.includes(key)) {
      delete formParams[key];
    }
  }
  return formParams;
}

const rootActions = {
  prepareFormData: async ({ state, dispatch }, { preset_data, master_data, request_params, position_information, recaptcha_setting, eop_contents }) => {

    // 別のLPで面談CPCLに存在しない雇用形態を選択してCV後に面談CPCLに遷移すると、雇用形態のバリデーションがエラーとならないため
    // ここで雇用形態の存在チェックを行い、存在しない場合はundefinedにする
    if (preset_data.employment_type) {
      preset_data.employment_type = master_data.employment_types.find(
        item => item.code == preset_data.employment_type
      )?.code;
    }

    dispatch('formData/initPresetData', preset_data);
    dispatch('masterData/initData', master_data);
    dispatch('requestParam/initData', request_params);
    dispatch('positionInformation/initData', position_information);
    dispatch('recaptchaSetting/initData', recaptcha_setting);
    dispatch('eopContents/initData', eop_contents);

    if (preset_data.prefecture_id) {
      await dispatch('masterData/fetchCitiesFromExternal', preset_data.prefecture_id);
    }
    if (preset_data.address) {
      const city = state.masterData.cities.find(city => city.label === preset_data.address);
      dispatch('formData/setCityId', city.value);
    }
  },
  submitForm({ state, dispatch, getters }) {
    // 二重送信防止用のフラグが立っていたら、送信しない。
    if (state.requestParam.is_processing) return;
    dispatch('requestParam/setIsProcessing', true);

    handlePostInquiryMail({
      inquiryUrl: state.requestParam.submit_form_url,
      thanksPageUrl: state.requestParam.thanks_page_url,
      requestBody: submitParams(state, getters),
      resolve: (response) => {
        redirectToThanksPageUrlWithParams({
          thanksPageUrl: state.requestParam.thanks_page_url,
          response: response
        })
      },
      reject: (error) => {
        dispatch('requestParam/setAuthenticityToken', error.response.data.authenticity_token);
      },
      finish: () => {
        dispatch('requestParam/setIsProcessing', false);
      }
    });
  }
};

export default createStore({
  getters: rootGetters,
  actions: rootActions,
  modules: {
    formData,
    requestParam,
    masterData,
    positionInformation,
    recaptchaSetting,
    eopContents
  }
});